import { useState, useEffect, useRef, useCallback } from 'react';
import './EpicSlider.scss';

// ─────────────────────────────────────────────────────────────
// EpicSlider
//
// Props:
//   slides  {Array}  each slide:
//   {
//     src:      {string}   image URL
//     alt:      {string}
//     label:    {string}   e.g. "New Season"
//     title:    {string}   main heading (words animate separately)
//     subtitle: {string}   small descriptor below title
//   }
//   autoPlay  {boolean}  default true
//   interval  {number}   ms between slides, default 6000
// ─────────────────────────────────────────────────────────────

const EpicSlider = ({ slides = [], autoPlay = true, interval = 6000 }) => {
  const [current, setCurrent]       = useState(0);
  const [prev, setPrev]             = useState(null);
  const [direction, setDirection]   = useState('next');
  const [transitioning, setTransitioning] = useState(false);
  const [paused, setPaused]         = useState(false);
  const [mousePos, setMousePos]     = useState({ x: 0.5, y: 0.5 });
  const [dragStart, setDragStart]   = useState(null);

  const containerRef = useRef(null);
  const total = slides.length;

  const go = useCallback((nextIndex, dir = 'next') => {
    if (transitioning || nextIndex === current) return;
    setDirection(dir);
    setPrev(current);
    setCurrent(nextIndex);
    setTransitioning(true);
    setTimeout(() => { setPrev(null); setTransitioning(false); }, 950);
  }, [current, transitioning]);

  const goNext = useCallback(() => go((current + 1) % total, 'next'), [go, current, total]);
  const goPrev = useCallback(() => go((current - 1 + total) % total, 'prev'), [go, current, total]);

  // Auto-play
  useEffect(() => {
    if (!autoPlay || paused || total <= 1) return;
    const t = setInterval(goNext, interval);
    return () => clearInterval(t);
  }, [autoPlay, paused, goNext, interval, total]);

  // Parallax mouse tracking
  const handleMouseMove = useCallback((e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    setMousePos({
      x: (e.clientX - rect.left) / rect.width,
      y: (e.clientY - rect.top)  / rect.height,
    });
  }, []);

  // Swipe support
  const handlePointerDown = (e) => setDragStart(e.clientX ?? e.touches?.[0]?.clientX);
  const handlePointerUp = (e) => {
    if (dragStart === null) return;
    const end = e.clientX ?? e.changedTouches?.[0]?.clientX;
    const delta = dragStart - end;
    if (Math.abs(delta) > 50) delta > 0 ? goNext() : goPrev();
    setDragStart(null);
  };

  if (!total) return null;

  const slide     = slides[current];
  const prevSlide = prev !== null ? slides[prev] : null;

  const px = (mousePos.x - 0.5) * -18;
  const py = (mousePos.y - 0.5) * -10;

  return (
    <section
      className="epic-slider"
      ref={containerRef}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onMouseMove={handleMouseMove}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onTouchStart={handlePointerDown}
      onTouchEnd={handlePointerUp}
    >
      {/* ── Cursor light ── */}
      <div
        className="epic-slider__cursor-light"
        style={{
          background: `radial-gradient(ellipse 60% 50% at ${mousePos.x * 100}% ${mousePos.y * 100}%,
            rgba(201,169,110,0.09) 0%, transparent 70%)`,
        }}
      />

      {/* ── Film grain ── */}
      {/* <div className="epic-slider__grain" /> */}

      {/* ── Slides ── */}
      <div className="epic-slider__track">

        {/* Exiting slide */}
        {prevSlide && (
          <div className={`epic-slider__slide epic-slider__slide--exit epic-slider__slide--exit-${direction}`}>
            <div
              className="epic-slider__bg"
              style={{ backgroundImage: `url(${prevSlide.src})` }}
            />
            <div className="epic-slider__vignette" />
          </div>
        )}

        {/* Entering slide */}
        <div
          className={`epic-slider__slide epic-slider__slide--enter epic-slider__slide--enter-${direction}`}
          key={current}
        >
          <div
            className="epic-slider__bg"
            style={{
              backgroundImage: `url(${slide.src})`,
              transform: window.innerWidth > 1024 
                ? `scale(1.06) translate(${px}px, ${py}px)` 
                : 'none',
              transition: 'transform 0.12s linear',
            }}
          />
          <div className="epic-slider__vignette" />

          {/* Copy */}
          {/* <div className="epic-slider__copy" key={`copy-${current}`}>
            {slide.label && (
              <span className="epic-slider__eyebrow">{slide.label}</span>
            )}
            <h2 className="epic-slider__title">
              {slide.title?.split(' ').map((word, i) => (
                <span
                  key={i}
                  className="epic-slider__word"
                  style={{ animationDelay: `${0.25 + i * 0.08}s` }}
                >
                  {word}
                </span>
              ))}
            </h2>
            {slide.subtitle && (
              <p
                className="epic-slider__subtitle"
                style={{ animationDelay: `${0.25 + ((slide.title?.split(' ').length || 0) * 0.08) + 0.1}s` }}
              >
                {slide.subtitle}
              </p>
            )}
          </div> */}
        </div>
      </div>

      {/* ── Vertical filmstrip ── */}
      <div className="epic-slider__filmstrip">
        {slides.map((s, i) => (
          <button
            key={i}
            className={`epic-slider__thumb${i === current ? ' is-active' : ''}`}
            onClick={() => go(i, i > current ? 'next' : 'prev')}
            aria-label={`Go to slide ${i + 1}`}
          >
            <img src={s.src} alt={s.alt || ''} className="epic-slider__thumb-img" draggable={false} />
            <div className="epic-slider__thumb-overlay" />
            {i === current && autoPlay && !paused && (
              <div
                className="epic-slider__thumb-bar"
                key={`bar-${current}`}
                style={{ animationDuration: `${interval}ms` }}
              />
            )}
          </button>
        ))}
      </div>

      {/* ── Arrows ── */}
      {total > 1 && (
        <div className="epic-slider__arrows">
          <button className="epic-slider__arrow" onClick={goPrev} aria-label="Previous slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M19 12H5M5 12l7-7M5 12l7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button className="epic-slider__arrow" onClick={goNext} aria-label="Next slide">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M5 12h14M19 12l-7-7M19 12l-7 7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      )}

      {/* ── Slide counter ── */}
      <div className="epic-slider__counter">
        <span className="epic-slider__counter-current" key={current}>
          {String(current + 1).padStart(2, '0')}
        </span>
        <span className="epic-slider__counter-sep" />
        <span className="epic-slider__counter-total">
          {String(total).padStart(2, '0')}
        </span>
      </div>

      {/* ── Bottom progress bar ── */}
      {autoPlay && !paused && (
        <div className="epic-slider__progress" key={current}>
          <div
            className="epic-slider__progress-fill"
            style={{ animationDuration: `${interval}ms` }}
          />
        </div>
      )}
    </section>
  );
};

export default EpicSlider;